import { AfterViewInit, Component, inject, Input, OnChanges, Output, SimpleChanges, EventEmitter, OnInit, ViewChild, ElementRef } from '@angular/core';
import { MemoryStorageService } from '../../services/memory-storage.service';
import { NewPlan } from '../../models/personalInfo';
import { PlanRuleMapper, PartnerLenderConfig } from '../../utils/emi-calculation-utils';
import { lastValueFrom } from 'rxjs';
import { AccountService } from '../../services/account.service';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { ISignUp } from '../../interface/interfaces';

@Component({
    selector: 'app-plan-calculator',
    templateUrl: './plan-calculator.component.html',
    styleUrls: ['./plan-calculator.component.scss']
})
export class PlanCalculatorComponent implements OnChanges, OnInit, AfterViewInit {

    private creditScore = MemoryStorageService.getItem('creditscore');
    private partnertToken = MemoryStorageService.getItem('accessToken') || '';
    private accountService = inject(AccountService);
    private spinner = inject(NgxSpinnerService);
    private storageData: ISignUp = {} as ISignUp;
    private router = inject(Router);

    public suggestedPlan: NewPlan = {} as NewPlan;
    public inPrincipleAmount: number = Number(MemoryStorageService.getItem('approvalAmount'));
    public intrestPercent = 0;
    public intrestAmount = 0;
    public tenor = 0;
    public isLoanAmoutSliderChanged: boolean = false;
    public tottalPayableAmount = 0;
    //public maxTenor = 0;
    private config: PartnerLenderConfig = {} as PartnerLenderConfig;
    public tenureList: number[] = [];
    public minAmount = 10000;
    public maxAmount = 1000000;
    public isSliderDisabled = false;

    @Input() public mobile: string = '';
    @Input() public sliderLoanAmount: number = 0;
    @Input() public lender: string = '';
    @Input() public plans: NewPlan[] = [];
    @Input() public isFromAssistPayment: boolean = false;
    @Output() public selectedPlan: EventEmitter<NewPlan> = new EventEmitter<NewPlan>();
    @ViewChild('inputMirror', { static: true }) inputMirror!: ElementRef;
    @ViewChild('loanInput', { static: true }) loanInput!: ElementRef;

    updateInputWidth(event: Event): void {
        const value = (event.target as HTMLInputElement).value || this.minAmount.toString();
        if (this.inputMirror && this.loanInput) {
            this.inputMirror.nativeElement.textContent = value;
            const mirrorWidth = this.inputMirror.nativeElement.offsetWidth;
            this.loanInput.nativeElement.style.width = `${mirrorWidth}px`;
        }
        this.onLoanAmountInput(event);
    }

    public ngAfterViewInit(): void {
        this.updateInputWidthFromValue();
    }

    public ngOnInit(): void {
        if (this.plans && this.plans.length > 0) {
            this.tenureList = this.plans.map(plan => plan.tenor).sort((a, b) => a - b);
            this.suggestedPlan = this.plans[this.plans.length - 1];
            console.log(this.suggestedPlan, "suggested Plan");
            //this.maxTenor = this.plans[this.plans.length - 1].tenor;
            this.intrestPercent = Number(this.suggestedPlan.roi.toFixed(2));
            this.intrestAmount = Number(this.suggestedPlan.interest_amount.toFixed(2));
            this.tenor = this.suggestedPlan.tenor;
            this.tottalPayableAmount = (this.suggestedPlan.emi_amount * this.suggestedPlan.tenor) +
                (this.suggestedPlan.gst + this.suggestedPlan.processing_fee);
        }

        if (this.mobile === '') {
            this.mobile = MemoryStorageService.getItem('mobile');
        }

        // Get the signup data using mobile.
        this.getSignUpDataByMobile();
        this.getEMIPlanConfiguration();
    }

    public ngOnChanges(simpleChanges: SimpleChanges) {
        if (simpleChanges) {
            this.sliderLoanAmount = Number(simpleChanges['sliderLoanAmount'].currentValue);
            this.updateInputWidthFromValue();
        }
    }

    public handleSliderChange(event: Event, sliderType: string): void {
        // const element = event.target as HTMLInputElement;
        // const value = Number(element.value);

        // Update the relevant slider value
        switch (sliderType) {
            case 'loan_amount':
                this.sliderLoanAmount = Number((event.target as HTMLInputElement).value);
                this.updateInputWidthFromValue();
                this.handleGeneratePlans(sliderType);
                break;
            case 'interest':
                break;
            case 'tenor':
                this.tenor = event as unknown as number;
                //this.tenor = this.getClosestTenure(value);
                this.handleTenureGeneratePlans();
                break;
        }
    }

    private typingTimeout: any;
    onLoanAmountInput(event: Event) {
        const element = event.target as HTMLInputElement;
        const value = Number(element.value);

        this.isSliderDisabled = true;

        if (this.typingTimeout) {
            clearTimeout(this.typingTimeout);
        }

        this.typingTimeout = setTimeout(() => {

            if (value >= this.minAmount && value <= Math.min(this.inPrincipleAmount, this.maxAmount)) {

            } else {
                // Optionally, reset to min or max value if out of bounds
                if (value < this.minAmount) {
                    this.sliderLoanAmount = this.minAmount;
                } else {
                    this.sliderLoanAmount = Math.min(this.inPrincipleAmount, this.maxAmount);
                }
            }

            this.isSliderDisabled = false;
            this.updateInputWidthFromValue();
            this.handleGeneratePlans();
        }, 2000);
    }

    updateInputWidthFromValue(): void {
        if (this.inputMirror && this.loanInput) {
            const value = this.sliderLoanAmount?.toString() || this.minAmount.toString();
            this.inputMirror.nativeElement.textContent = value;
            const mirrorWidth = this.inputMirror.nativeElement.offsetWidth;
            this.loanInput.nativeElement.style.width = `${mirrorWidth}px`;
        }
    }

    restrictInput(event: KeyboardEvent) {
        const inputValue = parseInt((event.target as HTMLInputElement).value);

        // If the value is less than the min value, prevent further input
        if (inputValue < this.minAmount) {
            (event.target as HTMLInputElement).value = this.minAmount.toString();
            event.preventDefault();
        }

        // If the value is greater than the max value, prevent further input
        if (inputValue > Math.min(this.inPrincipleAmount, this.maxAmount)) {
            (event.target as HTMLInputElement).value = Math.min(this.inPrincipleAmount, this.maxAmount).toString();
            event.preventDefault();
        }
    }

    public handleIncrease(slideType: string): void {
        switch (slideType) {
            case 'loan_amount':
                this.sliderLoanAmount += 1;
                this.updateInputWidthFromValue();
                this.handleGeneratePlans(slideType);
                break;
            case 'tenor':
                if (this.tenor >= this.tenureList[0]) {
                    const prevTenure = this.getNextTenure(this.tenor);
                    if (prevTenure) {
                        this.tenor = prevTenure;
                    }
                }
                this.handleTenureGeneratePlans();
                break;
            case 'interest':
                break;
        }

    }

    public handleDecrese(slideType: string): void {
        switch (slideType) {
            case 'loan_amount':
                this.sliderLoanAmount -= 1;
                this.updateInputWidthFromValue();
                this.handleGeneratePlans(slideType);
                break;
            case 'tenor':
                if (this.tenor > this.tenureList[0]) {
                    const prevTenure = this.getPrevTenure(this.tenor);
                    if (prevTenure) {
                        this.tenor = prevTenure;
                    }
                }
                this.handleTenureGeneratePlans();
                break;
            case 'interest':
                break;
        }

    }

    public async handleCalculateEMIPlan(): Promise<void> {
        this.spinner.show();
        try {
            //     const data = {
            //         mobileNo: this.storageData.mobileNo,
            //         lastName: this.storageData.lastName,
            //         firstName: this.storageData.firstName,
            //         email: this.storageData.email,
            //         monthlyIncome: this.storageData.monthlyIncome,
            //         travelType: this.storageData.travelType,
            //         travelDate: this.storageData.travelDate,
            //         origin: this.storageData.orgin,
            //         destination: this.storageData.destination,
            //         travelCost: this.storageData.travelCost,
            //         partnerTransactionId: 1,
            //         travelAgentName: this.storageData.travelAgentName,
            //         travelBrandName: this.storageData.travelBrandName,
            //         travelAgentEmailId: this.storageData.travelAgentEmailId,
            //         travelAgentMobile: this.storageData.travelAgentMobile,
            //     };

            //     if (!this.partnertToken) {
            //         this.partnertToken = MemoryStorageService.getItem('partnerToken');
            //     }

            //     this.tenureList = this.plans.map(plan => plan.tenor);
            //     const response = await lastValueFrom(this.accountService.getPlansFromSignUp(data, this.partnertToken));
            //     const responseData = response.data;
            // const selectedPlan = responseData.plan_details.filter(plan => plan.tenor === this.tenor)[0];
            const selectedPlan = await lastValueFrom(this.accountService.GetSelectedPlanDetails({ lender: this.lender, loanAmount: this.sliderLoanAmount, tenure: this.tenor }));

            if (this.isFromAssistPayment) {
                this.selectedPlan.emit(selectedPlan.data);
            } else {
                window.location.href = selectedPlan.data.navigate_url;
            }
        } catch (error) {
            console.log(error);
        }
        this.spinner.hide();
    }


    private handleGeneratePlans(type?: string): void {
        if (this.config && Object.keys(this.config).length > 0) {
            const plans = PlanRuleMapper.getSimplifiedTravelPlans(
                this.sliderLoanAmount,
                Number(this.creditScore),
                this.config.availableTenure || '',
                this.config.processingFee || '',
                this.config.processingFeeType || '',
                this.config.noCostTenure || '',
                this.config.noCostProcessingFee || '',
                this.config.noCostProcessingFeeType || '',
                this.config.roi || '',
                this.config.interestType || ''
            );

            console.log(plans, "generated plans");

            if (plans && plans.length > 0) {
                this.suggestedPlan = plans[plans.length - 1];
                this.tenureList = plans.map(plan => plan.tenor).sort((a, b) => a - b);
                this.tenor = this.suggestedPlan.tenor;

                //this.maxTenor = plans[plans.length - 1].tenor;
                this.intrestAmount = Number(this.suggestedPlan.interest_amount.toFixed(2));
                this.intrestPercent = Number(this.suggestedPlan.roi.toFixed(2));
                //this.tenor = this.suggestedPlan.tenor;
                this.tottalPayableAmount = (this.suggestedPlan.emi_amount * this.suggestedPlan.tenor) +
                    (this.suggestedPlan.gst + this.suggestedPlan.processing_fee);
            }
        }
    }

    private handleTenureGeneratePlans(): void {
        if (this.config && Object.keys(this.config).length > 0) {
            const plans = PlanRuleMapper.getSimplifiedTravelPlans(
                this.sliderLoanAmount,
                Number(this.creditScore),
                this.config.availableTenure || '',
                this.config.processingFee || '',
                this.config.processingFeeType || '',
                this.config.noCostTenure || '',
                this.config.noCostProcessingFee || '',
                this.config.noCostProcessingFeeType || '',
                this.config.roi || '',
                this.config.interestType || ''
            );

            console.log(plans, "generated plans");

            this.suggestedPlan = plans.filter(plan => plan.tenor === this.tenor)[0];

            if (this.suggestedPlan) {
                this.intrestAmount = Number(this.suggestedPlan.interest_amount.toFixed(2));
                this.intrestPercent = Number(this.suggestedPlan.roi.toFixed(2));
                this.tottalPayableAmount = (this.suggestedPlan.emi_amount * this.suggestedPlan.tenor) +
                    (this.suggestedPlan.gst + this.suggestedPlan.processing_fee);
            }
        } else if (this.plans && this.plans.length > 0) {
            this.suggestedPlan = this.plans.filter((plan: NewPlan) => plan.tenor === this.tenor)[0] || this.plans[0];
            if (this.suggestedPlan) {
                this.intrestAmount = Number(this.suggestedPlan.interest_amount.toFixed(2));
                this.intrestPercent = Number(this.suggestedPlan.roi.toFixed(2));
                this.tottalPayableAmount = (this.suggestedPlan.emi_amount * this.suggestedPlan.tenor) +
                    (this.suggestedPlan.gst + this.suggestedPlan.processing_fee);
            }
        }
    }


    private async getEMIPlanConfiguration(): Promise<void> {
        this.spinner.show();
        try {
            const configurationResponse = await lastValueFrom(this.accountService.GetEMIPlanConfiguration(this.lender));
            if (configurationResponse['data'] !== null) {
                const data = configurationResponse['data'];
                if (data?.type === 'plan') {
                    this.plans = data.plan;
                    if (this.plans && this.plans.length > 0) {
                        this.tenureList = this.plans.map((plan: NewPlan) => plan.tenor).sort((a, b) => a - b);
                        this.suggestedPlan = this.plans[this.plans.length - 1];
                        this.intrestPercent = Number(this.suggestedPlan.roi.toFixed(2));
                        this.intrestAmount = Number(this.suggestedPlan.interest_amount.toFixed(2));
                        this.tenor = this.suggestedPlan.tenor;
                        this.tottalPayableAmount = (this.suggestedPlan.emi_amount * this.suggestedPlan.tenor) +
                            (this.suggestedPlan.gst + this.suggestedPlan.processing_fee);
                        setTimeout(() => this.updateInputWidthFromValue(), 0);
                    }
                } else {
                    this.config = PlanRuleMapper.mapToPartnerLenderConfig(data);
                    this.minAmount = this.config.minAmount || 10000;
                    this.maxAmount = this.config.maxAmount || 1000000;
                    this.handleGeneratePlans();
                    setTimeout(() => this.updateInputWidthFromValue(), 0);
                }
            }
        } catch (error) {
            console.log(error);
        }
        this.spinner.hide();
    }

    getMaxAmount() {
        return Math.min(this.inPrincipleAmount, this.maxAmount);
    }

    private async getSignUpDataByMobile(): Promise<void> {
        try {
            let cleaned = this.mobile.replace(/^\+91/, "");
            const storageData = await lastValueFrom(this.accountService.GetSignInDataIfExist(cleaned));
            if (storageData) {
                this.storageData = storageData.data;
                this.generatePartnerToken();
            }
        } catch (error) {
            console.log(error);
        }
    }

    private async generatePartnerToken(): Promise<void> {
        let partnerAuthTokenRequest: any = {
            partnerId: this.storageData.agentRegId,
            apiKey: 'c3a3e4b9-4564-45c0-9698-d644a19cb9bA',
        };

        try {
            const response = await lastValueFrom(this.accountService.generatePartnerToken(partnerAuthTokenRequest));
            if (response['data']) {
                MemoryStorageService.setItem('partnerToken', response['data']);
            }
        } catch (error) {
            console.log(error);
        }
    }

    private getClosestTenure(value: number): number {
        let closest = this.tenureList[0];
        let minDiff = Math.abs(value - closest);

        for (let i = 1; i < this.tenureList.length; i++) {
            const diff = Math.abs(value - this.tenureList[i]);
            if (diff < minDiff) {
                closest = this.tenureList[i];
                minDiff = diff;
            }
        }

        return closest;
    }

    // Get next tenure from the available list
    private getNextTenure(current: number): number | null {
        const currentIndex = this.tenureList.indexOf(current);
        if (currentIndex < this.tenureList.length - 1) {
            return this.tenureList[currentIndex + 1];
        }
        return null;
    }

    // Get previous tenure from the available list
    private getPrevTenure(current: number): number | null {
        const currentIndex = this.tenureList.indexOf(current);
        if (currentIndex > 0) {
            return this.tenureList[currentIndex - 1];
        }
        return null;
    }
}
